import { Box, Divider, Icon, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "~/auth/AuthProvider";
import { canViewOwnCommissions, getCompanySettingsAccess } from "~/auth/roleChecks";
import sfAuth from "~/auth/SFAuth";
import useMyUser from "~/auth/useMyUser";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import UserAvatar from "~/components/UserAvatar";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useIsSiteRequiredRoute from "~/hooks/useIsSiteRequiredRoute";
import DisplayModeSwitcher from "~/layout/DisplayModeSwitcher";
import runGuardedExit from "~/layout/unsavedChangesRegistry";
import useInstallAppGate from "~/pwa/useInstallAppGate";
import useStandaloneApp from "~/pwa/useStandaloneApp";
import { openPreferencesModal, toggleProfilePanel } from "~/store/layoutSlice";
import { updateSite } from "~/store/siteSlice";

interface AccountMenuItem {
	action?: () => void;
	icon: string;
	label: string;
	path?: string;
}

const AccountMenu = () => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const currentSite = useAppSelector((state) => state.site.location);
	const siteRequired = useIsSiteRequiredRoute();
	const { currentUser } = useContext(AuthContext);
	const { myUser } = useMyUser();
	const { isConfined } = useStandaloneApp();
	const { canShowInstall } = useInstallAppGate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const closeMenu = () => setAnchorEl(null);

	const openAppInstalls = () => {
		closeMenu();
		dispatch(openPreferencesModal("app-installs"));
	};

	const canOpenCompanySettings = getCompanySettingsAccess(currentUser, myUser?.roles).canEnter;
	const showMyCommissions = canViewOwnCommissions(myUser?.roles);

	const navItems: AccountMenuItem[] = [
		{ action: () => dispatch(toggleProfilePanel()), icon: "person", label: t("accountMenu.profile") },
		{ action: () => dispatch(openPreferencesModal()), icon: "tune", label: t("accountMenu.preferences") },
		...(showMyCommissions
			? [
					{
						action: () => dispatch(openPreferencesModal("my-commissions")),
						icon: "percent",
						label: t("accountMenu.myCommissions")
					}
				]
			: []),
		...(!isConfined && canOpenCompanySettings
			? [{ icon: "admin_panel_settings", label: t("accountMenu.companySettings"), path: "/settings" }]
			: [])
	];

	return (
		<>
			<Tooltip title={t("accountMenu.account")}>
				<IconButton
					aria-expanded={Boolean(anchorEl)}
					aria-haspopup="menu"
					aria-label={t("accountMenu.menuAria")}
					onClick={(e) => setAnchorEl(e.currentTarget)}
					size="small"
				>
					<UserAvatar imageUrl={myUser?.profileImageUrl} name={currentUser?.displayName} size={30} />
				</IconButton>
			</Tooltip>
			<Menu
				anchorEl={anchorEl}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				onClose={closeMenu}
				open={Boolean(anchorEl)}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<Box sx={{ alignItems: "center", display: "flex", gap: 1.5, pb: 0.5, px: 2 }}>
					<UserAvatar imageUrl={myUser?.profileImageUrl} name={currentUser?.displayName} size={40} />
					<Box>
						{currentUser?.displayName && <BodyTextSmall bold>{currentUser.displayName}</BodyTextSmall>}
						{currentUser?.email && (
							<CaptionText sx={{ color: "text.secondary", display: "block" }}>{currentUser.email}</CaptionText>
						)}
						{currentSite && (
							<CaptionText sx={{ color: "text.secondary", display: "block" }}>{currentSite.name}</CaptionText>
						)}
					</Box>
				</Box>
				<Divider />
				{navItems.map(({ action, icon, label, path }) => (
					<MenuItem
						key={label}
						onClick={() => {
							closeMenu();
							if (action) action();
							else if (path) runGuardedExit(() => navigate(path));
						}}
					>
						<ListItemIcon>
							<Icon fontSize="small">{icon}</Icon>
						</ListItemIcon>
						<ListItemText>{label}</ListItemText>
					</MenuItem>
				))}
				{!isConfined && <DisplayModeSwitcher />}
				{!isConfined && canShowInstall && (
					<MenuItem onClick={openAppInstalls}>
						<ListItemIcon>
							<Icon fontSize="small">install_mobile</Icon>
						</ListItemIcon>
						<ListItemText>{t("accountMenu.appInstalls")}</ListItemText>
					</MenuItem>
				)}
				{siteRequired && (
					<MenuItem
						onClick={() => {
							closeMenu();
							runGuardedExit(() => dispatch(updateSite(null)));
						}}
					>
						<ListItemIcon>
							<Icon fontSize="small">location_on</Icon>
						</ListItemIcon>
						<ListItemText>{currentSite ? t("accountMenu.switchSite") : t("accountMenu.selectSite")}</ListItemText>
					</MenuItem>
				)}
				<Divider />
				<MenuItem
					onClick={() => {
						closeMenu();
						runGuardedExit(() => sfAuth.redirectToLogout());
					}}
				>
					<ListItemIcon>
						<Icon fontSize="small">logout</Icon>
					</ListItemIcon>
					<ListItemText>{t("accountMenu.signOut")}</ListItemText>
				</MenuItem>
			</Menu>
		</>
	);
};

export default AccountMenu;
